import CredentialWizardContent from '@/components/credentials/credentialWizard/CredentialWizardContent';
import CredentialEditWarning from './CredentialEditWarning.jsx';
import useCredentialEditWarning from './useCredentialEditWarning.js';
import Skeleton from '@/components/Skeleton.jsx';
import clsx from '@/lib/clsx';
import { getApps, getCredentialById } from '@/redux/actions/gravityActions';
import waitForSyncReady from '@/redux/actions/waitForSyncReady.js';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors';
import selectors from '@/redux/selectors/selectors';
import argumentDataSources from '@/schema/argumentDataSources.js';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { isPublishableApp } from 'shared/utils/isPublishableApp.js';

const CredentialWizard = forwardRef(({ credentialId, onClose, className = '' }, ref) => {
  const [loadedSession, setLoadedSession] = useState(null);
  const [failedSession, setFailedSession] = useState(null);
  const [initialSecrets, setInitialSecrets] = useState(null);

  const companyId = useSelector(selectors.companyId);
  const session = `${companyId}:${credentialId}`;
  const failed = failedSession === session;
  const loading = loadedSession !== session && !failed;
  const credential = useArgSelector(selectors.credentialById, credentialId);
  const appVersion = useSelector((state) => {
    const appMetadata = selectors.appsMetadataById(state, credential?.appId);
    return isPublishableApp(appMetadata?.slug) ? appMetadata?.publishedVersion || appMetadata?.version || 'draft' : 'draft';
  });
  const app = useSelector((state) => selectors.appById(state, credential?.appId, appVersion));
  const canEditCredentials = useSelector(selectors.canManageCompanyCredentials);
  const warning = useCredentialEditWarning({ credential, enabled: canEditCredentials });

  const dataSource = useMemo(() => {
    if (window.location.pathname.includes('/workflows')) {
      return argumentDataSources.workflowStep;
    }
    if (window.location.pathname.includes('/credentials')) {
      return argumentDataSources.credentialEditPage;
    }
    return argumentDataSources.home;
  }, [window.location.pathname]);

  useEffect(() => {
    let canceled = false;
    setLoadedSession(null);
    setFailedSession(null);
    if (!credentialId) {
      setLoadedSession(session);
      return;
    }
    const loadCredential = async () => {
      await waitForSyncReady();
      if (canceled) {
        return;
      }
      getCredentialById(credentialId, { secrets: true, companyId }, (data, error) => {
        if (!canceled) {
          if (error || !Array.isArray(data) || !data.some((row) => row?.credentialId === credentialId)) {
            setFailedSession(session);
          } else {
            setLoadedSession(session);
          }
        }
      });
    };
    loadCredential();
    return () => {
      canceled = true;
    };
  }, [credentialId, companyId, session]);

  useEffect(() => {
    if (credential?.appId && appVersion) {
      getApps({ versions: JSON.stringify([{ appId: credential?.appId, appVersion }]) });
    }
  }, [credential?.appId, appVersion]);

  useEffect(() => {
    const cleanSecrets = { ...credential?.secrets, label: undefined, custom: undefined };
    if (cleanSecrets && JSON.stringify(cleanSecrets) !== JSON.stringify(initialSecrets)) {
      setInitialSecrets(cleanSecrets);
    }
  }, [JSON.stringify(credential?.secrets)]);

  useEffect(() => {
    const body = document.querySelector('.credential-wizard .comp-wizard-body');
    if (!body) {
      return;
    }
    const handleScroll = () => {
      body.classList.toggle('has-scrolled', body.scrollTop > 0);
    };
    body.addEventListener('scroll', handleScroll);
    return () => {
      body.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className={clsx('credential-wizard', className)}>
      <CredentialEditWarning warning={warning} onCancel={onClose} />
      {loading && <Skeleton count={4} variant="light" />}
      {failed && <p role="alert">Unable to load this connection. Close and reopen it to try again.</p>}
      {!loading && !failed && !warning.blocked && <CredentialWizardContent key={`${companyId}:${credentialId}`} ref={ref} app={app} companyId={companyId} credentialId={credentialId} credential={credential} initialSecrets={initialSecrets} canEditCredentials={canEditCredentials} dataSource={dataSource} loading={loading} onClose={onClose} />}
    </div>
  );
});

CredentialWizard.displayName = 'CredentialWizard';

export default CredentialWizard;
